import { Button, Row, Col, Input, FormFeedback } from "reactstrap";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  addProductBenefit,
  deleteProductBenefit,
  updateProductBenefit,  
} from "services/insuranceService";

const KeyBenefitsForm = ({
  validation,
  setModal,
  allowDelete = false,
}: {
  validation: any;
  setModal: any;
  allowDelete?: boolean;
}) => {
  const benefits = validation.values.benefits || [];

  // Ensure there is always at least one benefit row to fill
  useEffect(() => {
    if (!benefits.length) {
      validation.setFieldValue("benefits", [
        { benfit_title: "", benfit_description: "", saved: false },
      ]);
    }
  }, [benefits.length, validation]);

  // Allow adding a new benefit only when all current rows are filled (non-empty)
  const canAddMore = benefits.every(
    (b: any) => b?.benfit_title?.trim() && b?.benfit_description?.trim()
  );

  const hasSavedBenefit = benefits.some((b: any) => b?.saved || b?.id);

  const addBenefit = () => {
    if (!canAddMore) {
      toast.error("Please fill the current benefit before adding more.");
      return;
    }
    validation.setFieldValue("benefits", [
      { benfit_title: "", benfit_description: "", saved: false },
      ...benefits,
    ]);
  };

  const withStatus = (payload: any) => ({
    ...payload,
    status: validation.values.status,  
  });

  const saveSingleBenefit = async (index: number) => {
    const benefit = validation.values.benefits[index];

    const title = benefit?.benfit_title?.trim();
    const desc = benefit?.benfit_description?.trim();

    if (!title || !desc) {
      toast.error("Please fill both Title & Description");
      return;
    }

    const payload = {
      product_id: validation.values.product_id || validation.values.id, 
      benfit_title: title,
      benfit_description: desc,
    };

    try {
      if (benefit?.id) {
        await updateProductBenefit(benefit.id, withStatus(payload));
        toast.success(`Benefit ${index + 1} updated successfully!`);
      } else {
        const res = await addProductBenefit(withStatus(payload));
        toast.success(`Benefit ${index + 1} saved successfully!`);
      }

      const updatedBenefits = [...validation.values.benefits];
      updatedBenefits[index] = { ...benefit, ...payload, saved: true };
      validation.setFieldValue("benefits", updatedBenefits);

    } catch (err) {
      console.log(err);
      toast.error("Error saving/update benefit");
    }
  };

  const deleteRow = async (index: number) => {
    const benefit = validation.values.benefits[index];
    try{
      if (benefit?.id) {
        await deleteProductBenefit(benefit.id);
        toast.success(`Benefit ${index + 1} deleted successfully!`);
      }

      const updatedBenefits = [...validation.values.benefits];
      updatedBenefits.splice(index, 1);
      validation.setFieldValue("benefits", updatedBenefits);
    }
    catch (err) {
      console.log(err);
      toast.error("Error deleting benefit!");
    }};

  return (
    <div>
      {hasSavedBenefit && canAddMore && (
        <div className="d-flex justify-content-end mb-3">
          <Button color="dark" outline onClick={addBenefit}>
            + Add Benefit
          </Button>
        </div>
      )}

      {validation.values.benefits?.map((benefit: any, index: number) => (
        <div key={index} className="p-3 border rounded mb-4 bg-light">
          <h6 className="fw-bold mb-2">Benefit {index + 1}</h6>

          <Row className="g-3">
            <Col md={11}>
              <Input
                placeholder="Benefit Title.."
                name={`benefits[${index}].benfit_title`}
                value={benefit.benfit_title}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.benefits?.[index]?.benfit_title &&
                  validation.errors.benefits?.[index]?.benfit_title
                }
                disabled={benefit?.saved}
                className="mb-2"
              />
              <FormFeedback>
                {validation.touched.benefits?.[index]?.benfit_title && 
                validation.errors.benefits?.[index]?.benfit_title}
              </FormFeedback>
              <Input
                type="textarea"
                placeholder="Benefit Description.."
                name={`benefits[${index}].benfit_description`}
                value={benefit.benfit_description}
                onChange={validation.handleChange}
                invalid={
                  validation.touched.benefits?.[index]?.benfit_description &&
                  validation.errors.benefits?.[index]?.benfit_description
                }
                disabled={benefit?.saved}
              />
              <FormFeedback>
                {validation.touched.benefits?.[index]?.benfit_description && 
                validation.errors.benefits?.[index]?.benfit_description}
              </FormFeedback>

              <Button
                color={benefit?.saved ? "secondary" : "primary"}
                className="mt-3"
                onClick={() => saveSingleBenefit(index)}
                disabled={benefit?.saved}
              >
                {benefit?.saved
                  ? "Saved"
                  : benefit?.id
                  ? "Update Benefit"
                  : "Save Benefit"}
              </Button>
            </Col>

            {allowDelete && (benefit?.id || benefit?.saved) && (
              <Col md={1} className="d-flex justify-content-end align-items-start">
                <Button
                  color="link"
                  className="p-0 text-danger"
                  onClick={() => deleteRow(index)}
                >
                  <i className="ri-delete-bin-line fs-4"></i>
                </Button>
              </Col>
            )}
          </Row>
        </div>
      ))}
    </div>
  );
};

export default KeyBenefitsForm;
